/**
 * EconomicCalendarStep Component
 *
 * Content-only component for displaying and managing tracked economic events
 * during the pre-trading ritual. Shows events the user has explicitly chosen
 * to track for their trading session.
 *
 * This component renders just the content (no Card wrapper) - the parent
 * component (PreTradingPhase) is responsible for providing the Card chrome.
 *
 * Phase 1: Shell with empty state, tracked events list, disabled "Add Events" CTA
 * Phase 2: API fetching, available events count, polling for released values
 * Phase 3: Add Events modal with selection
 * Phase 4: Delete action
 */

// 1. External dependencies
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';

// 2. Internal absolute imports
import { Button, Chip, Card, CardBody, EmptyState, Spinner, Select, SelectItem } from '@enfomentis/ui';
import { Calendar, Plus, AlertCircle, RefreshCw, Trash2, FileText, FilePlus, Pencil } from 'lucide-react';
import type { SlippageRisk } from '@/types/transient/NoteDraft';
import { noteDraftService } from '@/services/NoteDraftService';
import { formatTime, addDays, isEventOnDate } from '@/utils/dateHelpers';
import { COUNTRY_TO_CURRENCY } from '@/constants/economicCalendar';
import { economicCalendarService, isTransientError } from '@/services/EconomicCalendarService';
import { economicCalendarSettingsService } from '@/services/EconomicCalendarSettingsService';
import { AddEventsModal } from './AddEventsModal';
import { DeleteEventConfirmationModal } from './DeleteEventConfirmationModal';
import { DeleteObservationConfirmationModal } from './DeleteObservationConfirmationModal';
import { EconomicObservationModal } from './EconomicObservationModal';
import type { EconomicEventDraft } from '@/types/transient/EconomicEventDraft';
import type { EconomicCalendarSettings } from '@/types/schema/EconomicCalendarSettings';
import type { NoteDraft } from '@/types/transient/NoteDraft';

// =============================================================================
// Types
// =============================================================================

/** Timer type for browser compatibility */
type TimerId = ReturnType<typeof setTimeout>;

/** Error codes from EconomicCalendarService */
type FetchErrorCode =
  | 'UNAUTHORIZED'
  | 'BAD_REQUEST'
  | 'PROVIDER_RATE_LIMITED'
  | 'PROVIDER_AUTH_ERROR'
  | 'TRANSIENT'
  | 'UNKNOWN_ERROR';

/** Polling state structure */
interface PollingState {
  intervalId: TimerId;
  targetProviderEventId: string;
  startTime: number;
  currentIntervalMs: number;
}

/**
 * Props for EconomicCalendarStep component
 */
export interface EconomicCalendarStepProps {
  /** Tracked economic events (from SessionDraft.economicEvents) */
  trackedEvents: EconomicEventDraft[];
  /** Callback when tracked events change */
  onChange: (events: EconomicEventDraft[]) => void;
  /** User's display timezone for time formatting */
  displayTimezone: string;
  /** User's time format preference */
  timeFormat: '12h' | '24h';
  /** Session date in YYYY-MM-DD format for API queries */
  sessionDate: string;
  /** Key to trigger settings refresh (increment to re-fetch settings without re-fetching API events) */
  settingsRefreshKey?: number;
}

/**
 * Props for ImpactBadge sub-component
 */
interface ImpactBadgeProps {
  /** Impact level to display */
  level: 'low' | 'medium' | 'high' | undefined;
}

/**
 * Props for TrackedEventCard sub-component
 */
interface TrackedEventCardProps {
  /** The economic event to display */
  event: EconomicEventDraft;
  /** User's display timezone for time formatting */
  displayTimezone: string;
  /** User's time format preference */
  timeFormat: '12h' | '24h';
  /** General notes (not slippage) that include this event */
  generalNotes: NoteDraft[];
  /** Current slippage risk for this event cluster (undefined = not set) */
  slippageRisk: SlippageRisk | undefined;
  /** Callback when slippage changes */
  onSlippageChange: (eventId: string, slippageRisk: SlippageRisk | null) => void;
  /** Callback when observe button is clicked (add general note) */
  onObserve: (event: EconomicEventDraft) => void;
  /** Callback when delete button is clicked */
  onDelete: (eventId: string) => void;
  /** Callback when edit observation is clicked */
  onEditObservation: (observation: NoteDraft) => void;
  /** Callback when delete observation is clicked */
  onDeleteObservation: (observation: NoteDraft) => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format an economic event value for display
 * @param value - Raw value (stored as integer with precision)
 * @param precision - Number of decimal places (default 0)
 * @param unit - Unit suffix (e.g., "%", "K")
 * @returns Formatted string or undefined if value is undefined
 */
function formatEventValue(
  value: number | undefined,
  precision: number | undefined,
  unit: string | undefined
): string | undefined {
  if (value === undefined) return undefined;

  const effectivePrecision = precision ?? 0;
  const scaledValue = value / Math.pow(10, effectivePrecision);

  // Format with appropriate decimal places
  const formatted = effectivePrecision > 0
    ? scaledValue.toFixed(effectivePrecision)
    : scaledValue.toString();

  return unit ? `${formatted}${unit}` : formatted;
}

/**
 * Get the next upcoming event that needs polling
 * @param events - Tracked events array
 * @returns Next upcoming event or null
 */
function getNextUpcomingEvent(events: EconomicEventDraft[]): EconomicEventDraft | null {
  const now = Date.now();
  const upcoming = events.filter((e) => {
    if (e.source !== 'api') return false;
    if (!e.providerEventId) return false;
    if (e.actual !== undefined) return false;
    if (!e.datetime) return false;
    return new Date(e.datetime).getTime() > now;
  });

  if (upcoming.length === 0) return null;

  // Return earliest by datetime (datetime is guaranteed by filter above)
  return upcoming.reduce((earliest, current) => {
    const earliestTime = earliest.datetime ? new Date(earliest.datetime).getTime() : Infinity;
    const currentTime = current.datetime ? new Date(current.datetime).getTime() : Infinity;
    return currentTime < earliestTime ? current : earliest;
  });
}

/**
 * Get events that are past due and need actual values
 * @param events - Tracked events array
 * @returns Array of past-due events
 */
function getPastDueEvents(events: EconomicEventDraft[]): EconomicEventDraft[] {
  const now = Date.now();
  return events.filter((e) => {
    if (e.source !== 'api') return false;
    if (!e.providerEventId) return false;
    if (e.actual !== undefined) return false;
    if (!e.datetime) return false;
    return new Date(e.datetime).getTime() <= now;
  });
}

/**
 * Merge tracked events with fresh API data
 * Only updates API-sourced events with providerEventId
 * @param tracked - Current tracked events
 * @param fresh - Fresh events from API
 * @returns New array with merged data (immutable)
 */
function mergeTrackedEventsWithFresh(
  tracked: EconomicEventDraft[],
  fresh: EconomicEventDraft[]
): EconomicEventDraft[] {
  // Build lookup map from fresh events
  const freshMap = new Map<string, EconomicEventDraft>();
  for (const event of fresh) {
    if (event.providerEventId) {
      freshMap.set(event.providerEventId, event);
    }
  }

  // Merge fresh data into tracked events
  return tracked.map((trackedEvent) => {
    // Only merge API events with providerEventId
    if (trackedEvent.source !== 'api' || !trackedEvent.providerEventId) {
      return trackedEvent;
    }

    const freshEvent = freshMap.get(trackedEvent.providerEventId);
    if (!freshEvent) {
      return trackedEvent;
    }

    // Copy data fields from fresh event using conditional spread
    // (satisfies exactOptionalPropertyTypes - don't assign undefined to optional fields)
    return {
      ...trackedEvent,
      ...(freshEvent.actual !== undefined && { actual: freshEvent.actual }),
      ...(freshEvent.forecast !== undefined && { forecast: freshEvent.forecast }),
      ...(freshEvent.previous !== undefined && { previous: freshEvent.previous }),
      ...(freshEvent.unit !== undefined && { unit: freshEvent.unit }),
      ...(freshEvent.valuePrecision !== undefined && { valuePrecision: freshEvent.valuePrecision }),
      updatedAt: new Date().toISOString(),
    };
  });
}

/**
 * Get polling interval based on elapsed time
 * @param elapsedMs - Milliseconds since polling started
 * @returns Interval in ms, or null to stop polling
 */
function getPollingInterval(elapsedMs: number): number | null {
  if (elapsedMs < 60000) {
    // 0-1 min: every 5 seconds
    return 5000;
  } else if (elapsedMs < 300000) {
    // 1-5 min: every 15 seconds
    return 15000;
  } else if (elapsedMs < 3600000) {
    // 5-60 min: every 60 seconds
    return 60000;
  } else {
    // >60 min: stop polling
    return null;
  }
}

/**
 * Compute available events count (filtered, excluding already tracked)
 * @param cached - Cached API events
 * @param tracked - Currently tracked events
 * @param settings - User settings for filtering
 * @returns Count of available events
 */
function computeAvailableCount(
  cached: EconomicEventDraft[],
  tracked: EconomicEventDraft[],
  settings: EconomicCalendarSettings | null
): number {
  if (!settings) return 0;

  // Empty impactLevels means "show none" per schema semantics
  if (settings.impactLevels.length === 0) return 0;

  // Build set of tracked providerEventIds
  const trackedIds = new Set<string>(
    tracked
      .map((e) => e.providerEventId)
      .filter((id): id is string => !!id)
  );

  // Filter cached events
  let filtered = cached;

  // Apply country filter (if non-empty)
  if (settings.countryFilters.length > 0) {
    const countrySet = new Set(settings.countryFilters.map((c) => c.toUpperCase()));
    filtered = filtered.filter((e) => e.country && countrySet.has(e.country.toUpperCase()));
  }

  // Apply impact filter (if non-empty)
  if (settings.impactLevels.length > 0) {
    const impactSet = new Set(settings.impactLevels);
    filtered = filtered.filter((e) => e.impactLevel && impactSet.has(e.impactLevel));
  }

  // Exclude already tracked
  filtered = filtered.filter((e) => !e.providerEventId || !trackedIds.has(e.providerEventId));

  return filtered.length;
}

// =============================================================================
// Sub-Components
// =============================================================================

/**
 * Impact level color mapping for dot indicator
 */
const IMPACT_DOT_COLORS: Record<'low' | 'medium' | 'high', string> = {
  low: 'bg-default-400',
  medium: 'bg-warning-500',
  high: 'bg-danger-500',
};

/**
 * Impact level labels for accessibility
 */
const IMPACT_LABELS: Record<'low' | 'medium' | 'high', string> = {
  low: 'Low impact',
  medium: 'Medium impact',
  high: 'High impact',
};

/**
 * Displays impact level as a small colored dot
 * - low: gray dot
 * - medium: amber dot
 * - high: red dot
 * - undefined: no dot rendered
 */
const ImpactDot: React.FC<ImpactBadgeProps> = ({ level }) => {
  if (!level) return null;

  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${IMPACT_DOT_COLORS[level]}`}
      aria-label={IMPACT_LABELS[level]}
      title={IMPACT_LABELS[level]}
    />
  );
};

/**
 * Slippage risk dropdown options
 */
const SLIPPAGE_DROPDOWN_OPTIONS: { key: string; label: string }[] = [
  { key: 'none', label: 'None' },
  { key: 'low', label: 'Low' },
  { key: 'moderate', label: 'Moderate' },
  { key: 'high', label: 'High' },
  { key: 'extreme', label: 'Extreme' },
];

/**
 * Context display labels for observation chips
 */
const CONTEXT_LABELS: Record<string, string> = {
  'pre-trading': 'Pre-Trading',
  'live-trading': 'Live-Trading',
  'post-trading': 'Post-Trading',
};

/**
 * Displays a single tracked economic event
 * Shows impact dot, time, name, slippage dropdown, notes badge, and action buttons
 * Expandable to show general notes when they exist
 */
const TrackedEventCard: React.FC<TrackedEventCardProps> = ({
  event,
  displayTimezone,
  timeFormat,
  generalNotes,
  slippageRisk,
  onSlippageChange,
  onObserve,
  onDelete,
  onEditObservation,
  onDeleteObservation,
}) => {
  // Derived values
  const hasGeneralNotes = generalNotes.length > 0;
  const noteCount = generalNotes.length;

  // State: cards start collapsed
  const [isExpanded, setIsExpanded] = useState(false);

  // Format time (handle missing/invalid datetime)
  const formattedTime = useMemo(() => {
    if (!event.datetime) return '--:--';
    try {
      return formatTime(event.datetime, displayTimezone, timeFormat);
    } catch {
      return '--:--';
    }
  }, [event.datetime, displayTimezone, timeFormat]);

  // Get currency for country (normalize to uppercase for lookup)
  const currency = event.country
    ? COUNTRY_TO_CURRENCY[event.country.toUpperCase() as keyof typeof COUNTRY_TO_CURRENCY]
    : undefined;

  // Format country display: "US (USD)" or just country code if no currency mapping
  const countryDisplay = event.country
    ? currency
      ? `${event.country} (${currency})`
      : event.country
    : undefined;

  // Format data values
  const actualFormatted = formatEventValue(event.actual, event.valuePrecision, event.unit);
  const forecastFormatted = formatEventValue(event.forecast, event.valuePrecision, event.unit);
  const previousFormatted = formatEventValue(event.previous, event.valuePrecision, event.unit);

  // Check if we have any data values to display
  const hasDataValues = actualFormatted || forecastFormatted || previousFormatted;

  // Check if event has released (has actual value)
  const hasReleased = event.actual !== undefined;

  /**
   * Compute dropdown items including Clear option when slippage is set
   */
  const slippageDropdownItems = useMemo(() => {
    const items = [...SLIPPAGE_DROPDOWN_OPTIONS];
    if (slippageRisk) {
      items.push({ key: 'clear', label: 'Clear' });
    }
    return items;
  }, [slippageRisk]);

  /**
   * Handle slippage dropdown selection change
   */
  const handleSlippageSelection = useCallback(
    (keys: 'all' | Set<React.Key>) => {
      if (keys === 'all') return;
      const selectedKey = Array.from(keys)[0] as string | undefined;

      if (selectedKey === 'clear') {
        // Clear option selected - delete slippage note
        onSlippageChange(event.id, null);
      } else if (selectedKey) {
        // Slippage level selected
        onSlippageChange(event.id, selectedKey as SlippageRisk);
      }
    },
    [event.id, onSlippageChange]
  );

  return (
    <Card shadow="sm" radius="md">
      <CardBody className="py-3 px-3">
        {/* Top row: Event info + Delete (mobile) or All controls (desktop) */}
        <div className="flex items-center gap-3">
          {/* Event info: dot, time, details */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Impact dot (Step 1) */}
            <div className="flex-shrink-0 w-3">
              <ImpactDot level={event.impactLevel} />
            </div>

            {/* Time */}
            <div className="flex-shrink-0 w-14 text-sm font-mono text-text-secondary">
              {formattedTime}
            </div>

            {/* Event info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {/* Event name */}
                <span className="text-text-primary truncate">
                  {event.name || 'Untitled event'}
                </span>

                {/* Country with currency */}
                {countryDisplay && (
                  <>
                    <span className="text-text-tertiary hidden sm:inline">•</span>
                    <span className="text-sm text-text-secondary hidden sm:inline">
                      {countryDisplay}
                    </span>
                  </>
                )}
              </div>

              {/* Data values row */}
              {hasDataValues && (
                <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                  {actualFormatted && (
                    <span className={hasReleased ? 'text-primary-600 font-medium' : 'text-text-secondary'}>
                      <span className="text-text-tertiary">Act:</span> {actualFormatted}
                    </span>
                  )}
                  {forecastFormatted && (
                    <span className="text-text-secondary">
                      <span className="text-text-tertiary">Est:</span> {forecastFormatted}
                    </span>
                  )}
                  {previousFormatted && (
                    <span className="text-text-secondary">
                      <span className="text-text-tertiary">Prev:</span> {previousFormatted}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Desktop: All controls inline */}
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            {/* Slippage dropdown */}
            <div className="w-28">
              <Select
                size="sm"
                variant="bordered"
                placeholder="Slippage"
                aria-label="Slippage risk assessment"
                selectedKeys={slippageRisk ? new Set([slippageRisk]) : new Set()}
                onSelectionChange={handleSlippageSelection}
                classNames={{
                  trigger: 'min-h-8 h-8',
                  value: 'text-xs',
                }}
              >
                {slippageDropdownItems.map((option) => (
                  <SelectItem
                    key={option.key}
                    className={option.key === 'clear' ? 'text-danger' : undefined}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </div>

            {/* View notes button with badge */}
            {hasGeneralNotes && (
              <div className="relative">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="primary"
                  onPress={() => setIsExpanded(!isExpanded)}
                  aria-label={`${isExpanded ? 'Collapse' : 'View'} ${noteCount} note${noteCount > 1 ? 's' : ''} for ${event.name || 'event'}`}
                >
                  <FileText size={16} />
                </Button>
                <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-medium rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                  {noteCount}
                </span>
              </div>
            )}

            {/* Add note button */}
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="primary"
              onPress={() => onObserve(event)}
              aria-label={`Add note for ${event.name || 'event'}`}
            >
              <FilePlus size={16} />
            </Button>

            {/* Delete button */}
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => onDelete(event.id)}
              aria-label={`Delete ${event.name || 'event'}`}
            >
              <Trash2 size={16} />
            </Button>
          </div>

          {/* Mobile: Just delete button (right-aligned) */}
          <Button
            className="sm:hidden flex-shrink-0"
            isIconOnly
            size="sm"
            variant="light"
            color="danger"
            onPress={() => onDelete(event.id)}
            aria-label={`Delete ${event.name || 'event'}`}
          >
            <Trash2 size={16} />
          </Button>
        </div>

        {/* Mobile: Slippage + Notes row (full width, spread out) */}
        <div className="flex sm:hidden items-center gap-2 mt-3">
          {/* Slippage dropdown - grows to fill */}
          <div className="flex-1">
            <Select
              size="sm"
              variant="bordered"
              placeholder="Slippage"
              aria-label="Slippage risk assessment"
              selectedKeys={slippageRisk ? new Set([slippageRisk]) : new Set()}
              onSelectionChange={handleSlippageSelection}
              classNames={{
                trigger: 'min-h-8 h-8',
                value: 'text-xs',
              }}
            >
              {slippageDropdownItems.map((option) => (
                <SelectItem
                  key={option.key}
                  className={option.key === 'clear' ? 'text-danger' : undefined}
                >
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          </div>

          {/* View notes button with badge */}
          {hasGeneralNotes && (
            <div className="relative">
              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="primary"
                onPress={() => setIsExpanded(!isExpanded)}
                aria-label={`${isExpanded ? 'Collapse' : 'View'} ${noteCount} note${noteCount > 1 ? 's' : ''} for ${event.name || 'event'}`}
              >
                <FileText size={16} />
              </Button>
              <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-medium rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                {noteCount}
              </span>
            </div>
          )}

          {/* Add note button */}
          <Button
            isIconOnly
            size="sm"
            variant="light"
            color="primary"
            onPress={() => onObserve(event)}
            aria-label={`Add note for ${event.name || 'event'}`}
          >
            <FilePlus size={16} />
          </Button>
        </div>

        {/* Expanded general notes section (Step 4: only general notes, not slippage) */}
        {isExpanded && hasGeneralNotes && (
          <div className="mt-3 pt-3 border-t border-divider space-y-2">
            {generalNotes.map((note) => (
              <Card key={note.id} shadow="none" radius="sm" className="bg-content2/50">
                <CardBody className="p-2">
                  <div className="flex items-start gap-2">
                    {/* Note content */}
                    <div className="flex-1 min-w-0">
                      {/* Context chip */}
                      <div className="flex flex-wrap gap-1 mb-1">
                        <Chip size="sm" variant="flat" color="default">
                          {CONTEXT_LABELS[note.captureContext] || note.captureContext}
                        </Chip>
                      </div>
                      {/* Notes text */}
                      {note.text && (
                        <p className="text-sm text-text-secondary whitespace-pre-wrap break-words">
                          {note.text}
                        </p>
                      )}
                      {/* No content indicator */}
                      {!note.text && (
                        <p className="text-sm text-text-tertiary italic">
                          No content
                        </p>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={() => onEditObservation(note)}
                        aria-label="Edit note"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        color="danger"
                        onPress={() => onDeleteObservation(note)}
                        aria-label="Delete note"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

// =============================================================================
// Main Component
// =============================================================================

/**
 * EconomicCalendarStep Component
 *
 * Displays tracked economic events for the trading session.
 * Phase 2: Fetches events from API, polls for released values, shows available count.
 */
export const EconomicCalendarStep: React.FC<EconomicCalendarStepProps> = ({
  trackedEvents,
  onChange,
  displayTimezone,
  timeFormat,
  sessionDate,
  settingsRefreshKey,
}) => {
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  /** Cached API events (raw, unfiltered) */
  const [cachedApiEvents, setCachedApiEvents] = useState<EconomicEventDraft[]>([]);

  /** Loading state for initial fetch */
  const [isLoading, setIsLoading] = useState(false);

  /** Error from fetch operation */
  const [fetchError, setFetchError] = useState<FetchErrorCode | null>(null);

  /** Retry counter to force effect re-run */
  const [retryCounter, setRetryCounter] = useState(0);

  /** Modal open state */
  const [isModalOpen, setIsModalOpen] = useState(false);

  /** Event being deleted (for confirmation modal) */
  const [deletingEvent, setDeletingEvent] = useState<EconomicEventDraft | null>(null);

  /** Event being observed (for observation modal) */
  const [observingEvent, setObservingEvent] = useState<EconomicEventDraft | null>(null);

  /** Economic calendar settings (reactive state for filter recomputation) */
  const [settings, setSettings] = useState<EconomicCalendarSettings | null>(null);

  /** All economic observations (loaded once, refreshed on changes) */
  const [allObservations, setAllObservations] = useState<NoteDraft[]>([]);

  /** Observation being edited (for modal edit mode) */
  const [editingObservation, setEditingObservation] = useState<NoteDraft | null>(null);

  /** Observation being deleted (for confirmation) */
  const [deletingObservation, setDeletingObservation] = useState<NoteDraft | null>(null);

  // -------------------------------------------------------------------------
  // Refs
  // -------------------------------------------------------------------------

  /** Previous settingsRefreshKey for detecting changes */
  const prevSettingsRefreshKeyRef = useRef<number | undefined>(settingsRefreshKey);

  /** Last fetched date (to detect date changes) */
  const lastFetchedDateRef = useRef<string | null>(null);

  /** Wake-up timer for next upcoming event */
  const wakeUpTimerRef = useRef<TimerId | null>(null);

  /** Active polling state */
  const pollingStateRef = useRef<PollingState | null>(null);

  /** Flag to track if component is mounted (for async safety) */
  const isMountedRef = useRef(true);

  // -------------------------------------------------------------------------
  // Derived Values
  // -------------------------------------------------------------------------

  // Sort tracked events by datetime ascending (undefined/invalid sorts last)
  const sortedEvents = useMemo(() => {
    return [...trackedEvents].sort((a, b) => {
      const aTime = a.datetime ? new Date(a.datetime).getTime() : Infinity;
      const bTime = b.datetime ? new Date(b.datetime).getTime() : Infinity;
      const aValid = Number.isFinite(aTime) ? aTime : Infinity;
      const bValid = Number.isFinite(bTime) ? bTime : Infinity;
      if (aValid === bValid) return 0;
      return aValid - bValid;
    });
  }, [trackedEvents]);

  // Compute available count (reactive to settings changes)
  const availableCount = useMemo(() => {
    return computeAvailableCount(cachedApiEvents, trackedEvents, settings);
  }, [cachedApiEvents, trackedEvents, settings]);

  // Check if API is enabled (reactive to settings changes)
  const apiEnabled = settings?.apiEnabled ?? true;

  // Compute tracked event IDs for modal
  const trackedEventIds = useMemo(() => {
    return new Set<string>(
      trackedEvents
        .map((e) => e.providerEventId)
        .filter((id): id is string => !!id)
    );
  }, [trackedEvents]);

  // Get initial filter values from settings (with defaults, reactive to settings changes)
  const initialCountryFilters = settings?.countryFilters ?? [];
  const initialImpactLevels = settings?.impactLevels ?? ['low', 'medium', 'high'];

  /**
   * Get the slippage note for an event (note with slippageRisk defined)
   * Returns the first slippage note found for any same-time event cluster
   */
  const getSlippageNoteForEvent = useCallback(
    (eventId: string): NoteDraft | undefined => {
      return allObservations.find(
        (obs) =>
          obs.slippageRisk !== undefined &&
          (obs.eventIds?.includes(eventId) ?? false)
      );
    },
    [allObservations]
  );

  /**
   * Get general notes for an event (notes WITHOUT slippageRisk)
   * Returns notes where eventIds includes the event's id
   */
  const getGeneralNotesForEvent = useCallback(
    (eventId: string): NoteDraft[] => {
      return allObservations.filter(
        (obs) =>
          obs.slippageRisk === undefined &&
          (obs.eventIds?.includes(eventId) ?? false)
      );
    },
    [allObservations]
  );

  /**
   * Load all economic observations from service
   * @returns The loaded observations (empty array on error) for immediate use by caller
   */
  const loadObservations = useCallback(async (): Promise<NoteDraft[]> => {
    try {
      const all = await noteDraftService.getAll();
      // Filter to only economic type notes
      const economicNotes = all.filter((note) => note.noteType === 'economic');
      setAllObservations(economicNotes);
      return economicNotes;
    } catch (error) {
      console.error('Failed to load economic observations:', error);
      // Don't show error to user - observations are enhancement, not critical
      // Intentionally keep last known observations visible (don't clear state on local read failures)
      return []; // Return empty array so callers don't crash
    }
  }, []);

  /**
   * Get all same-time event IDs for a given event
   * Returns event IDs (including the given event) that share the same datetime
   */
  const getSameTimeEventIds = useCallback(
    (eventId: string): string[] => {
      const event = trackedEvents.find((e) => e.id === eventId);
      if (!event?.datetime) return [eventId];

      return trackedEvents
        .filter((e) => e.datetime === event.datetime)
        .map((e) => e.id);
    },
    [trackedEvents]
  );

  /**
   * Sync slippage notes' eventIds with current tracked events.
   * Called when trackedEvents changes to ensure slippage notes include all same-time events.
   *
   * @param observations - Optional observations to sync against. If not provided, uses allObservations state.
   *                       Pass fresh observations from loadObservations() to avoid stale closure issues.
   */
  const syncSlippageNotesWithTrackedEvents = useCallback(
    async (observations?: NoteDraft[]) => {
      // Use provided observations or fall back to state
      const observationsToSync = observations ?? allObservations;

      // Get all slippage notes (notes with slippageRisk defined)
      const slippageNotes = observationsToSync.filter(
        (obs) => obs.slippageRisk !== undefined
      );
      if (slippageNotes.length === 0) return;

      // Build a set of all tracked event IDs for quick lookup
      const trackedEventIdSet = new Set(trackedEvents.map((e) => e.id));

      for (const note of slippageNotes) {
        // Find the owner event
        const ownerEvent = trackedEvents.find((e) => e.id === note.ownerId);
        if (!ownerEvent) {
          // Owner no longer exists - this will be handled by deletion cascade
          continue;
        }

        // Get expected same-time event IDs
        const expectedEventIds = trackedEvents
          .filter((e) => e.datetime === ownerEvent.datetime)
          .map((e) => e.id)
          .sort(); // Sort for stable comparison

        // Get current eventIds (filtered to only still-tracked events)
        const currentEventIds = (note.eventIds ?? [])
          .filter((id) => trackedEventIdSet.has(id))
          .sort();

        // Check if they differ
        const needsUpdate =
          expectedEventIds.length !== currentEventIds.length ||
          expectedEventIds.some((id, i) => id !== currentEventIds[i]);

        if (needsUpdate) {
          try {
            await noteDraftService.update(note.id, {
              eventIds: expectedEventIds,
            });
            // Optimistic update
            setAllObservations((prev) =>
              prev.map((obs) =>
                obs.id === note.id ? { ...obs, eventIds: expectedEventIds } : obs
              )
            );
          } catch (error) {
            console.error('Failed to sync slippage note eventIds:', error);
            // Refresh from service on error
            void loadObservations();
          }
        }
      }
    },
    [allObservations, trackedEvents, loadObservations]
  );

  /**
   * Cascade notes when an event is deleted.
   * - Reassigns owner if deleted event was the owner and other events exist
   * - Soft-deletes notes if deleted event was the only event
   * - Removes deleted event from eventIds otherwise
   */
  const cascadeNotesForDeletedEvent = useCallback(
    async (deletedEventId: string) => {
      // Find all notes that reference the deleted event
      const affectedNotes = allObservations.filter(
        (obs) => obs.eventIds?.includes(deletedEventId) ?? false
      );

      if (affectedNotes.length === 0) return;

      // Helper to pick new owner deterministically: earliest datetime → name → id
      const pickNewOwner = (eventIds: string[]): string | null => {
        const candidates = trackedEvents.filter(
          (e) => eventIds.includes(e.id) && e.id !== deletedEventId
        );
        if (candidates.length === 0) return null;

        candidates.sort((a, b) => {
          // Sort by datetime first
          const dateA = a.datetime ?? '';
          const dateB = b.datetime ?? '';
          if (dateA !== dateB) return dateA.localeCompare(dateB);
          // Then by name
          const nameA = (a.name ?? '').toLowerCase();
          const nameB = (b.name ?? '').toLowerCase();
          if (nameA !== nameB) return nameA.localeCompare(nameB);
          // Then by id
          return a.id.localeCompare(b.id);
        });

        return candidates[0]?.id ?? null;
      };

      for (const note of affectedNotes) {
        const currentEventIds = note.eventIds ?? [];
        const remainingEventIds = currentEventIds.filter((id) => id !== deletedEventId);
        const isOwnerDeleted = note.ownerId === deletedEventId;

        try {
          if (isOwnerDeleted && remainingEventIds.length > 0) {
            // Reassign owner to another event
            const newOwnerId = pickNewOwner(currentEventIds);
            if (newOwnerId) {
              await noteDraftService.update(note.id, {
                ownerId: newOwnerId,
                eventIds: remainingEventIds,
              });
              // Optimistic update
              setAllObservations((prev) =>
                prev.map((obs) =>
                  obs.id === note.id
                    ? { ...obs, ownerId: newOwnerId, eventIds: remainingEventIds }
                    : obs
                )
              );
            } else {
              // No valid owner found (remaining eventIds are stale) - soft delete
              await noteDraftService.softDelete(note.id);
              // Optimistic update
              setAllObservations((prev) => prev.filter((obs) => obs.id !== note.id));
            }
          } else if (isOwnerDeleted && remainingEventIds.length === 0) {
            // No remaining events - soft delete the note
            await noteDraftService.softDelete(note.id);
            // Optimistic update
            setAllObservations((prev) => prev.filter((obs) => obs.id !== note.id));
          } else {
            // Owner not deleted, just remove from eventIds
            await noteDraftService.update(note.id, {
              eventIds: remainingEventIds,
            });
            // Optimistic update
            setAllObservations((prev) =>
              prev.map((obs) =>
                obs.id === note.id ? { ...obs, eventIds: remainingEventIds } : obs
              )
            );
          }
        } catch (error) {
          console.error('Failed to cascade note for deleted event:', error);
          // Refresh from service on error
          void loadObservations();
        }
      }
    },
    [allObservations, trackedEvents, loadObservations]
  );

  /** Ref to track if initial observations have been loaded (for sync effect guard) */
  const hasInitialLoadRef = useRef(false);

  /** Ref to hold sync function (avoids stale closure in mount effect) */
  const syncSlippageNotesRef = useRef(syncSlippageNotesWithTrackedEvents);
  syncSlippageNotesRef.current = syncSlippageNotesWithTrackedEvents;

  // -------------------------------------------------------------------------
  // Polling Functions (using refs to avoid circular dependencies)
  // -------------------------------------------------------------------------

  /** Ref to hold startPolling function (avoids circular dependency) */
  const startPollingRef = useRef<((targetProviderEventId: string) => void) | null>(null);

  /** Ref to hold pollForUpdates function (avoids circular dependency) */
  const pollForUpdatesRef = useRef<(() => Promise<void>) | null>(null);

  /** Stop polling and schedule next event */
  const stopPollingAndScheduleNext = useCallback(() => {
    // Clear current polling
    if (pollingStateRef.current) {
      clearInterval(pollingStateRef.current.intervalId);
      pollingStateRef.current = null;
    }

    // Find next upcoming event
    const nextEvent = getNextUpcomingEvent(trackedEvents);
    if (nextEvent && nextEvent.providerEventId && nextEvent.datetime) {
      const delay = new Date(nextEvent.datetime).getTime() - Date.now();

      if (delay <= 0) {
        // Event already past due, start polling immediately
        startPollingRef.current?.(nextEvent.providerEventId);
      } else {
        // Schedule wake-up timer
        wakeUpTimerRef.current = setTimeout(() => {
          if (isMountedRef.current && nextEvent.providerEventId) {
            startPollingRef.current?.(nextEvent.providerEventId);
          }
        }, delay);
      }
    }
  }, [trackedEvents]);

  /** Poll for updates */
  const pollForUpdates = useCallback(async () => {
    if (!pollingStateRef.current || !isMountedRef.current) return;

    const { targetProviderEventId, startTime, currentIntervalMs } = pollingStateRef.current;
    const elapsed = Date.now() - startTime;
    const nextInterval = getPollingInterval(elapsed);

    // Check if we should stop polling
    if (nextInterval === null) {
      stopPollingAndScheduleNext();
      return;
    }

    // Fetch fresh events (±1 day range to cover all timezone offsets)
    const result = await economicCalendarService.fetchEvents(
      addDays(sessionDate, -1),
      addDays(sessionDate, 1),
      []
    );

    if (!isMountedRef.current) return;

    if (result.ok) {
      // Filter to only events on session date in user's display timezone (single source of truth)
      const filteredEvents = result.events.filter((e) =>
        isEventOnDate(e.datetime, sessionDate, displayTimezone)
      );

      // Update cache with filtered events
      setCachedApiEvents(filteredEvents);

      // Merge into tracked events
      const merged = mergeTrackedEventsWithFresh(trackedEvents, result.events);

      // Check if any changes occurred (all merged fields)
      const hasChanges = merged.some((m, i) => {
        const orig = trackedEvents[i];
        return m.actual !== orig?.actual ||
               m.forecast !== orig?.forecast ||
               m.previous !== orig?.previous ||
               m.unit !== orig?.unit ||
               m.valuePrecision !== orig?.valuePrecision;
      });

      if (hasChanges) {
        onChange(merged);
      }

      // Check if target event still exists and has actual
      const targetEvent = merged.find((e) => e.providerEventId === targetProviderEventId);
      if (!targetEvent) {
        // Target event was removed, stop polling and schedule next
        stopPollingAndScheduleNext();
        return;
      }
      if (targetEvent.actual !== undefined) {
        stopPollingAndScheduleNext();
        return;
      }

      // Check if interval needs to change
      if (nextInterval !== currentIntervalMs && pollingStateRef.current) {
        // Clear old interval first
        clearInterval(pollingStateRef.current.intervalId);

        // Start new interval
        const newIntervalId = setInterval(() => {
          void pollForUpdatesRef.current?.();
        }, nextInterval);

        pollingStateRef.current = {
          ...pollingStateRef.current,
          intervalId: newIntervalId,
          currentIntervalMs: nextInterval,
        };
      }
    }
    // On error, continue polling (don't stop for transient errors)
  }, [sessionDate, displayTimezone, trackedEvents, onChange, stopPollingAndScheduleNext]);

  // Update ref when pollForUpdates changes
  pollForUpdatesRef.current = pollForUpdates;

  /** Start polling for a specific event */
  const startPolling = useCallback((targetProviderEventId: string) => {
    // Clear any existing wake-up timer
    if (wakeUpTimerRef.current) {
      clearTimeout(wakeUpTimerRef.current);
      wakeUpTimerRef.current = null;
    }

    // Clear any existing polling
    if (pollingStateRef.current) {
      clearInterval(pollingStateRef.current.intervalId);
    }

    // Get initial interval (5000ms)
    const initialInterval = getPollingInterval(0) ?? 5000;

    // Start interval
    const intervalId = setInterval(() => {
      void pollForUpdatesRef.current?.();
    }, initialInterval);

    // Initialize polling state
    pollingStateRef.current = {
      intervalId,
      targetProviderEventId,
      startTime: Date.now(),
      currentIntervalMs: initialInterval,
    };

    // Poll immediately on start
    void pollForUpdatesRef.current?.();
  }, []);

  // Update ref when startPolling changes
  startPollingRef.current = startPolling;

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load observations on mount, then sync to catch events added during loading
  useEffect(() => {
    const load = async () => {
      const freshObservations = await loadObservations();
      hasInitialLoadRef.current = true;
      // Sync using fresh observations via ref (avoids stale closure)
      void syncSlippageNotesRef.current(freshObservations);
    };
    void load();
  }, [loadObservations]);

  // Sync slippage notes when trackedEvents or allObservations changes (after initial load)
  useEffect(() => {
    // Skip if initial observations haven't been loaded yet
    if (!hasInitialLoadRef.current) return;
    // Skip if no observations to sync
    if (allObservations.length === 0) return;

    void syncSlippageNotesWithTrackedEvents();
  }, [trackedEvents, syncSlippageNotesWithTrackedEvents, allObservations]);

  // Clear stale cache immediately when sessionDate or displayTimezone changes
  // This prevents modal from showing previous day's events during fetch
  useEffect(() => {
    // Only clear if we have cached events from a different date
    if (lastFetchedDateRef.current && lastFetchedDateRef.current !== sessionDate) {
      setCachedApiEvents([]);
    }
  }, [sessionDate, displayTimezone]);

  // Initial fetch on mount/date change
  useEffect(() => {
    // Skip if already fetched for this date
    if (lastFetchedDateRef.current === sessionDate) return;

    const fetchEvents = async () => {
      // Load settings
      try {
        const freshSettings = await economicCalendarSettingsService.getCurrentSettings();
        setSettings(freshSettings);

        // Check if API is disabled
        if (!freshSettings.apiEnabled) {
          // Clear any stale state from previous fetch
          setCachedApiEvents([]);
          setFetchError(null);
          setIsLoading(false);
          lastFetchedDateRef.current = sessionDate;
          return;
        }
      } catch {
        // If settings fail, assume API enabled but clear stale cache
        setSettings(null);
        setCachedApiEvents([]);
      }

      // Fetch events (±1 day range to cover all timezone offsets)
      setIsLoading(true);
      setFetchError(null);

      const result = await economicCalendarService.fetchEvents(
        addDays(sessionDate, -1),
        addDays(sessionDate, 1),
        []
      );

      if (!isMountedRef.current) return;

      if (result.ok) {
        // Filter to only events on session date in user's display timezone (single source of truth)
        const filteredEvents = result.events.filter((e) =>
          isEventOnDate(e.datetime, sessionDate, displayTimezone)
        );

        setCachedApiEvents(filteredEvents);
        setFetchError(null);

        // Check for past-due events and start polling
        const pastDue = getPastDueEvents(trackedEvents);
        if (pastDue.length > 0 && pastDue[0]?.providerEventId) {
          startPolling(pastDue[0].providerEventId);
        }
      } else {
        setFetchError(result.error);
      }

      setIsLoading(false);
      lastFetchedDateRef.current = sessionDate;
    };

    void fetchEvents();
  }, [sessionDate, displayTimezone, trackedEvents, startPolling, retryCounter]);

  // Settings refresh effect - re-fetches settings when settingsRefreshKey changes
  // Does NOT re-fetch API events, just recomputes filters using cached events
  useEffect(() => {
    // Skip initial run (only respond to key changes)
    if (prevSettingsRefreshKeyRef.current === settingsRefreshKey) return;
    const prevKey = prevSettingsRefreshKeyRef.current;
    prevSettingsRefreshKeyRef.current = settingsRefreshKey;

    // Skip if this is initial mount (prevKey was undefined, key is now defined)
    if (prevKey === undefined) return;

    const refreshSettings = async () => {
      try {
        const prevApiEnabled = settings?.apiEnabled ?? true;
        const freshSettings = await economicCalendarSettingsService.getCurrentSettings();
        setSettings(freshSettings);

        // Handle apiEnabled flips
        if (!prevApiEnabled && freshSettings.apiEnabled) {
          // false → true: trigger event fetch
          lastFetchedDateRef.current = null;
          setRetryCounter((prev) => prev + 1);
        } else if (prevApiEnabled && !freshSettings.apiEnabled) {
          // true → false: clear cached events and error state
          setCachedApiEvents([]);
          setFetchError(null);
        }
        // If apiEnabled unchanged, availableCount memo will recompute automatically
      } catch (err) {
        console.error('Failed to refresh economic calendar settings:', err);
        // Keep existing settings on error
      }
    };

    void refreshSettings();
  }, [settingsRefreshKey, settings?.apiEnabled]);

  // Wake-up scheduling effect
  useEffect(() => {
    // Clear existing wake-up timer
    if (wakeUpTimerRef.current) {
      clearTimeout(wakeUpTimerRef.current);
      wakeUpTimerRef.current = null;
    }

    // Skip if polling is already active
    if (pollingStateRef.current) return;

    // Find next upcoming event
    const nextEvent = getNextUpcomingEvent(trackedEvents);

    if (nextEvent && nextEvent.providerEventId && nextEvent.datetime) {
      const delay = new Date(nextEvent.datetime).getTime() - Date.now();

      if (delay <= 0) {
        // Event already past due, start polling immediately
        startPolling(nextEvent.providerEventId);
      } else {
        // Schedule wake-up timer
        const providerEventId = nextEvent.providerEventId;
        wakeUpTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            startPolling(providerEventId);
          }
        }, delay);
      }
    } else {
      // No upcoming event - check for past-due events needing actual values
      const pastDue = getPastDueEvents(trackedEvents);
      if (pastDue.length > 0 && pastDue[0]?.providerEventId) {
        startPolling(pastDue[0].providerEventId);
      }
    }

    // Cleanup
    return () => {
      if (wakeUpTimerRef.current) {
        clearTimeout(wakeUpTimerRef.current);
        wakeUpTimerRef.current = null;
      }
    };
  }, [trackedEvents, startPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear wake-up timer
      if (wakeUpTimerRef.current) {
        clearTimeout(wakeUpTimerRef.current);
      }
      // Clear polling interval
      if (pollingStateRef.current) {
        clearInterval(pollingStateRef.current.intervalId);
      }
    };
  }, []);

  // -------------------------------------------------------------------------
  // Event Handlers
  // -------------------------------------------------------------------------

  /** Retry fetch after error */
  const handleRetry = useCallback(() => {
    lastFetchedDateRef.current = null; // Defensive: reset guard
    setFetchError(null);
    setIsLoading(true);
    setRetryCounter(c => c + 1); // Trigger effect re-run
  }, []);

  /** Open the Add Events modal */
  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  /** Close the Add Events modal */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  /** Handle events tracked from modal */
  const handleTrackEvents = useCallback((newEvents: EconomicEventDraft[]) => {
    // Append new events to existing tracked events
    const updatedEvents = [...trackedEvents, ...newEvents];
    onChange(updatedEvents);
    handleCloseModal();
  }, [trackedEvents, onChange, handleCloseModal]);

  /** Handle delete button click - opens confirmation modal */
  const handleDeleteClick = useCallback((event: EconomicEventDraft) => {
    setDeletingEvent(event);
  }, []);

  /** Handle delete confirmation */
  const handleDeleteConfirm = useCallback(async () => {
    // Defensive: no-op if no event to delete
    if (!deletingEvent) return;

    // Cascade notes before removing the event
    await cascadeNotesForDeletedEvent(deletingEvent.id);

    const filteredEvents = trackedEvents.filter((e) => e.id !== deletingEvent.id);
    onChange(filteredEvents);
    setDeletingEvent(null);
  }, [deletingEvent, trackedEvents, onChange, cascadeNotesForDeletedEvent]);

  /** Handle delete cancellation */
  const handleDeleteCancel = useCallback(() => {
    setDeletingEvent(null);
  }, []);

  /** Handle observe button click - opens observation modal */
  const handleObserveClick = useCallback((event: EconomicEventDraft) => {
    setObservingEvent(event);
  }, []);

  /** Handle observation modal close */
  const handleObserveClose = useCallback(() => {
    setObservingEvent(null);
    setEditingObservation(null);
  }, []);

  /** Handle observation modal save success - refresh observations list */
  const handleObservationSaved = useCallback(() => {
    void loadObservations();
    handleObserveClose();
  }, [loadObservations, handleObserveClose]);

  /** Handle edit observation click - opens modal in edit mode */
  const handleEditObservation = useCallback((observation: NoteDraft) => {
    // Find the triggering event for this observation
    const triggeringEvent = trackedEvents.find((e) => e.id === observation.ownerId);
    if (triggeringEvent) {
      setObservingEvent(triggeringEvent);
      setEditingObservation(observation);
    }
  }, [trackedEvents]);

  /** Handle delete observation click - opens confirmation */
  const handleDeleteObservationClick = useCallback((observation: NoteDraft) => {
    setDeletingObservation(observation);
  }, []);

  /** Handle delete observation confirmation */
  const handleDeleteObservationConfirm = useCallback(async () => {
    if (!deletingObservation) return;

    try {
      await noteDraftService.softDelete(deletingObservation.id);
      // Update local state immediately (optimistic)
      setAllObservations((prev) =>
        prev.filter((obs) => obs.id !== deletingObservation.id)
      );
    } catch (error) {
      console.error('Failed to delete observation:', error);
      // Refresh from service on error to sync state
      void loadObservations();
    } finally {
      setDeletingObservation(null);
    }
  }, [deletingObservation, loadObservations]);

  /** Handle delete observation cancellation */
  const handleDeleteObservationCancel = useCallback(() => {
    setDeletingObservation(null);
  }, []);

  /**
   * Handle inline slippage dropdown change
   * Creates, updates, or deletes slippage note for the event cluster
   */
  const handleSlippageChange = useCallback(
    async (eventId: string, newSlippageRisk: SlippageRisk | null) => {
      const existingNote = getSlippageNoteForEvent(eventId);
      const sameTimeIds = getSameTimeEventIds(eventId);

      try {
        if (newSlippageRisk === null) {
          // Clear slippage - delete the existing note
          if (existingNote) {
            await noteDraftService.softDelete(existingNote.id);
            // Optimistic update
            setAllObservations((prev) =>
              prev.filter((obs) => obs.id !== existingNote.id)
            );
          }
        } else if (existingNote) {
          // Update existing slippage note
          await noteDraftService.update(existingNote.id, {
            slippageRisk: newSlippageRisk,
            eventIds: sameTimeIds, // Keep eventIds in sync with current same-time events
          });
          // Optimistic update
          setAllObservations((prev) =>
            prev.map((obs) =>
              obs.id === existingNote.id
                ? { ...obs, slippageRisk: newSlippageRisk, eventIds: sameTimeIds }
                : obs
            )
          );
        } else {
          // Create new slippage note
          const newNote = await noteDraftService.add({
            noteType: 'economic',
            ownerId: eventId,
            timestamp: new Date().toISOString(),
            captureContext: 'pre-trading',
            text: '',
            slippageRisk: newSlippageRisk,
            eventIds: sameTimeIds,
          });
          // Optimistic update
          setAllObservations((prev) => [...prev, newNote]);
        }
      } catch (error) {
        console.error('Failed to update slippage:', error);
        // Refresh from service on error to sync state
        void loadObservations();
      }
    },
    [getSlippageNoteForEvent, getSameTimeEventIds, loadObservations]
  );

  // -------------------------------------------------------------------------
  // Render Helpers
  // -------------------------------------------------------------------------

  /** Get CTA button text */
  const getCtaText = () => {
    if (!apiEnabled || fetchError) {
      return 'Add Events';
    }
    if (isLoading) {
      return 'Add Events';
    }
    if (availableCount > 0) {
      return `Add Events (${availableCount} meet filter criteria)`;
    }
    return 'Add Events';
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // Loading state
  if (isLoading && sortedEvents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <Spinner size="md" />
        <p className="text-sm text-text-secondary">Loading economic events...</p>
      </div>
    );
  }

  // Error state (only show if no events and error)
  if (fetchError && sortedEvents.length === 0) {
    const errorMessage = isTransientError(fetchError)
      ? 'Unable to load events. Tap to retry.'
      : 'Economic calendar unavailable';

    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center py-6 gap-3">
          <AlertCircle className="text-danger-500" size={32} />
          <p className="text-sm text-text-secondary text-center">{errorMessage}</p>
          {isTransientError(fetchError) && (
            <Button
              size="sm"
              variant="flat"
              color="primary"
              startContent={<RefreshCw size={14} />}
              onPress={handleRetry}
            >
              Retry
            </Button>
          )}
        </div>
        <div className="flex justify-center">
          <Button
            color="primary"
            variant="flat"
            startContent={<Plus size={16} />}
            onPress={handleOpenModal}
          >
            Add Events
          </Button>
        </div>
        {/* Add Events Modal */}
        <AddEventsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          availableEvents={cachedApiEvents}
          trackedEventIds={trackedEventIds}
          initialCountryFilters={initialCountryFilters}
          initialImpactLevels={initialImpactLevels}
          onTrackEvents={handleTrackEvents}
          displayTimezone={displayTimezone}
          timeFormat={timeFormat}
          sessionDate={sessionDate}
        />
      </div>
    );
  }

  // Empty state when no tracked events
  if (sortedEvents.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={<Calendar size={24} />}
          title="No events tracked"
          description="Add economic events to track for today's session"
        />
        <div className="flex justify-center">
          <Button
            color="primary"
            variant="flat"
            startContent={<Plus size={16} />}
            onPress={handleOpenModal}
          >
            {getCtaText()}
          </Button>
        </div>
        {/* Add Events Modal */}
        <AddEventsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          availableEvents={cachedApiEvents}
          trackedEventIds={trackedEventIds}
          initialCountryFilters={initialCountryFilters}
          initialImpactLevels={initialImpactLevels}
          onTrackEvents={handleTrackEvents}
          displayTimezone={displayTimezone}
          timeFormat={timeFormat}
          sessionDate={sessionDate}
        />
      </div>
    );
  }

  // Tracked events list
  return (
    <div className="space-y-4">
      {/* Events list */}
      <div className="space-y-3">
        {sortedEvents.map((event) => (
          <TrackedEventCard
            key={event.id}
            event={event}
            displayTimezone={displayTimezone}
            timeFormat={timeFormat}
            generalNotes={getGeneralNotesForEvent(event.id)}
            slippageRisk={getSlippageNoteForEvent(event.id)?.slippageRisk}
            onSlippageChange={(eventId, slippageRisk) => void handleSlippageChange(eventId, slippageRisk)}
            onObserve={handleObserveClick}
            onDelete={() => handleDeleteClick(event)}
            onEditObservation={handleEditObservation}
            onDeleteObservation={handleDeleteObservationClick}
          />
        ))}
      </div>

      {/* Add Events CTA */}
      <div className="flex justify-center pt-2">
        <Button
          color="primary"
          variant="flat"
          startContent={isLoading ? <Spinner size="sm" /> : <Plus size={16} />}
          onPress={handleOpenModal}
        >
          {getCtaText()}
        </Button>
      </div>

      {/* Add Events Modal */}
      <AddEventsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        availableEvents={cachedApiEvents}
        trackedEventIds={trackedEventIds}
        initialCountryFilters={initialCountryFilters}
        initialImpactLevels={initialImpactLevels}
        onTrackEvents={handleTrackEvents}
        displayTimezone={displayTimezone}
        timeFormat={timeFormat}
        sessionDate={sessionDate}
      />

      {/* Delete Confirmation Modal */}
      <DeleteEventConfirmationModal
        isOpen={deletingEvent !== null}
        event={deletingEvent}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      {/* Economic Note Modal (general notes only - slippage handled inline) */}
      <EconomicObservationModal
        isOpen={observingEvent !== null}
        onClose={handleObserveClose}
        onSave={handleObservationSaved}
        triggeringEvent={observingEvent}
        trackedEvents={trackedEvents}
        displayTimezone={displayTimezone}
        timeFormat={timeFormat}
        editingObservation={editingObservation}
      />

      {/* Delete Observation Confirmation Modal */}
      <DeleteObservationConfirmationModal
        isOpen={deletingObservation !== null}
        onClose={handleDeleteObservationCancel}
        onConfirm={() => void handleDeleteObservationConfirm()}
      />
    </div>
  );
};

export default EconomicCalendarStep;
